import { ref, type Ref } from 'vue';
import { AssetHierarchyLevelsService, type AssetHierarchyLevelDto, type InsertAssetHierarchyLevelCommand } from '@/shared/services/assetHierarchyLevels';
import { AssetGeneralInfosService, type AssetGeneralInfoDto } from '@/shared/services/assetGeneralInfo';
import { AssetTreeService } from '@/shared/services/assets-tree';
import { useAssetHierarchyRootPosition } from '@/features/asset-hierarchy/composables/useAssetHierarchyRootPosition';

export interface InitializationState {
  isLoading: boolean;
  error: string | null;
  step: 'check' | 'level' | 'asset' | 'complete';
  preSeededLevel: AssetHierarchyLevelDto | null;
  createdLevel: AssetHierarchyLevelDto | null;
  createdAsset: AssetGeneralInfoDto | null;
}

export interface UseAssetHierarchyInitializationReturn {
  state: Ref<InitializationState>;
  needsInitialization: Ref<boolean>;
  
  // Methods
  checkInitializationNeeded: () => Promise<boolean>;
  getPreSeededRootLevel: () => Promise<AssetHierarchyLevelDto | null>;
  createHierarchyLevel: (command: InsertAssetHierarchyLevelCommand) => Promise<AssetHierarchyLevelDto>;
  createFirstAsset: (assetData: Partial<AssetGeneralInfoDto>) => Promise<AssetGeneralInfoDto>;
  completeInitialization: () => Promise<void>;
  proceedToAssetCreation: () => void;
  resetState: () => void;
}

/**
 * Module-level state for singleton behavior.
 * Shared across all callers (page component + router guard) to prevent
 * race conditions where different instances have different state.
 */
const state = ref<InitializationState>({
  isLoading: false,
  error: null,
  step: 'check',
  preSeededLevel: null,
  createdLevel: null,
  createdAsset: null
});

const needsInitialization = ref(false);

/**
 * Composable for handling Asset Hierarchy initialization flow
 */
export function useAssetHierarchyInitialization(): UseAssetHierarchyInitializationReturn {
  const { getRootInfo, clearRootInfoCache } = useAssetHierarchyRootPosition();

  function getErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof Error && error.message) return error.message;
    return fallback;
  }

  /**
   * Check if Asset Hierarchy initialization is needed
   * Returns true if AssetTree is empty and no usable root exists.
   * Always refresh root info so imports and other out-of-band mutations
   * are reflected immediately in router-guard decisions.
   */
  async function checkInitializationNeeded(): Promise<boolean> {
    state.value.isLoading = true;
    state.value.error = null;
    state.value.step = 'check';

    try {
      // Resolve smallest hierarchy-level position to use as root.
      // Treat "no levels" (or no usable positions) as "needs initialization".
      const rootInfo = await getRootInfo({ forceRefresh: true });
      state.value.preSeededLevel = rootInfo.rootLevel;

      if (!rootInfo.hasLevels) {
        needsInitialization.value = true;
        return true;
      }

      // First check if there are any root assets at the resolved root position
      const rootResponse = await AssetTreeService.getChildren({
        position: rootInfo.rootPosition,
        page: 1,
        pageSize: 1,
      });

      // If root assets exist, no initialization needed
      if (rootResponse.data && rootResponse.data.length > 0) {
        needsInitialization.value = false;
        return false;
      }

      // If we have a pre-seeded level, we still need initialization (to create first asset)
      // If no pre-seeded level, we need full initialization (level + asset)
      needsInitialization.value = true;
      return true;

    } catch (error: unknown) {
      state.value.error = getErrorMessage(error, 'Failed to check initialization status');
      console.error('Error checking initialization:', error);
      // Assume initialization is needed if we can't check
      needsInitialization.value = true;
      return true;
    } finally {
      state.value.isLoading = false;
    }
  }

  /**
   * Get pre-seeded root AssetHierarchyLevel (smallest Position number)
   */
  async function getPreSeededRootLevel(): Promise<AssetHierarchyLevelDto | null> {
    try {
      const rootInfo = await getRootInfo({ forceRefresh: true });
      return rootInfo.rootLevel;
    } catch (error: unknown) {
      console.error('Error getting pre-seeded root level:', error);
      return null;
    }
  }

  /**
   * Create a new AssetHierarchyLevel
   */
  async function createHierarchyLevel(command: InsertAssetHierarchyLevelCommand): Promise<AssetHierarchyLevelDto> {
    state.value.isLoading = true;
    state.value.error = null;
    state.value.step = 'level';

    try {
      const createdLevel = await AssetHierarchyLevelsService.insert(command);
      state.value.createdLevel = createdLevel;
      return createdLevel;
    } catch (error: unknown) {
      state.value.error = getErrorMessage(error, 'Failed to create hierarchy level');
      throw error;
    } finally {
      state.value.isLoading = false;
    }
  }

  /**
   * Create the first AssetGeneralInfo
   */
  async function createFirstAsset(assetData: Partial<AssetGeneralInfoDto>): Promise<AssetGeneralInfoDto> {
    state.value.isLoading = true;
    state.value.error = null;
    state.value.step = 'asset';

    try {
      // Ensure we have a levelId from either pre-seeded or created level
      const levelId = state.value.createdLevel?.id || state.value.preSeededLevel?.id;
      if (!levelId) {
        throw new Error('No hierarchy level available for asset creation');
      }

      const assetToCreate = {
        ...assetData,
        levelId: levelId,
        parentId: null // Root asset has no parent
      };

      const createdAsset = await AssetGeneralInfosService.create(assetToCreate);
      state.value.createdAsset = createdAsset;
      return createdAsset;
    } catch (error: unknown) {
      state.value.error = getErrorMessage(error, 'Failed to create first asset');
      throw error;
    } finally {
      state.value.isLoading = false;
    }
  }

  /**
   * Complete the initialization process
   */
  async function completeInitialization(): Promise<void> {
    state.value.step = 'complete';
    needsInitialization.value = false;
    
    // Clear any temporary state but keep created items for reference
    state.value.error = null;
    state.value.isLoading = false;
    
    // Clear the root position cache so navigation guard re-checks fresh data
    clearRootInfoCache();
  }

  /**
   * Proceed to asset creation step (used when pre-seeded level exists)
   */
  function proceedToAssetCreation(): void {
    state.value.step = 'asset';
    state.value.error = null;
  }

  /**
   * Reset the initialization state
   */
  function resetState(): void {
    state.value = {
      isLoading: false,
      error: null,
      step: 'check',
      preSeededLevel: null,
      createdLevel: null,
      createdAsset: null
    };
    needsInitialization.value = false;
  }

  return {
    state,
    needsInitialization,
    checkInitializationNeeded,
    getPreSeededRootLevel,
    createHierarchyLevel,
    createFirstAsset,
    completeInitialization,
    proceedToAssetCreation,
    resetState
  };
}
